import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DollarSign, MapPin, Clock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const LivePriceQuoteTool = () => {
  const { toast } = useToast();
  const [pickupAddress, setPickupAddress] = useState("");
  const [dropoffAddress, setDropoffAddress] = useState("");
  const [pickupTime, setPickupTime] = useState("");
  const [isCalculating, setIsCalculating] = useState(false);
  const [quoteResult, setQuoteResult] = useState<{
    price: number;
    distance: number;
    duration: string;
    minimumApplied?: boolean;
    lateNightFee?: number;
  } | null>(null);

  // Pricing constants
  const RATE_PER_MILE = 3.50;
  const BOOKING_FEE = 15.00;
  const GRATUITY_MULTIPLIER = 1.20;
  const SAT_MINIMUM_FARE = 85.00;
  const LATE_NIGHT_FEE = 25.00;

  const isLateNightTime = (time: string): boolean => {
    if (!time) return false;
    const [hours] = time.split(':').map(Number);
    return hours >= 23 || hours < 5;
  };

  const calculateQuote = async () => {
    // Validation
    if (!pickupAddress.trim() || !dropoffAddress.trim()) {
      toast({
        title: "Missing Information",
        description: "Please enter both pickup and drop-off addresses.",
        variant: "destructive",
      });
      return;
    }

    // Check if Google Maps is loaded
    // @ts-ignore - Google Maps is loaded via script tag
    if (typeof window.google === 'undefined' || !window.google.maps) {
      toast({
        title: "Maps Not Available",
        description: "Google Maps is not loaded. Please refresh the page.",
        variant: "destructive",
      });
      return;
    }

    setIsCalculating(true);
    setQuoteResult(null);

    try {
      // @ts-ignore - Google Maps is loaded via script tag
      const service = new window.google.maps.DistanceMatrixService();
      
      // @ts-ignore - Google Maps is loaded via script tag
      const request = {
        origins: [pickupAddress],
        destinations: [dropoffAddress],
        // @ts-ignore
        travelMode: window.google.maps.TravelMode.DRIVING,
        // @ts-ignore
        unitSystem: window.google.maps.UnitSystem.IMPERIAL,
      };

      // @ts-ignore - Google Maps is loaded via script tag
      service.getDistanceMatrix(request, (response, status) => {
        setIsCalculating(false);

        if (status === 'OK' && response) {
          const element = response.rows[0]?.elements[0];

          if (element && element.status === 'OK') {
            // Get distance in miles
            const distanceInMeters = element.distance?.value || 0;
            const distanceInMiles = distanceInMeters / 1609.34;

            // PRICING FORMULA (EXACT):
            // 1. Mileage Cost: distance_in_miles * 3.50
            // 2. Subtotal: Mileage Cost + 15.00 (Booking Fee)
            // 3. Apply $85 minimum for SAT Airport routes (before gratuity)
            // 4. Add late night fee ($25) if pickup is 11 PM - 5 AM
            // 5. Total Price: Subtotal * 1.20 (20% Gratuity)
            
            const mileageCost = distanceInMiles * RATE_PER_MILE;
            let subtotal = mileageCost + BOOKING_FEE;
            
            // Check if this is a SAT Airport route and apply minimum fare
            const pickupLower = pickupAddress.toLowerCase();
            const dropoffLower = dropoffAddress.toLowerCase();
            const isSATRoute = pickupLower.includes('sat') || pickupLower.includes('san antonio international') ||
                              dropoffLower.includes('sat') || dropoffLower.includes('san antonio international') ||
                              pickupLower.includes('airport') || dropoffLower.includes('airport');
            
            const minimumApplied = isSATRoute && subtotal < SAT_MINIMUM_FARE;
            if (minimumApplied) {
              subtotal = SAT_MINIMUM_FARE;
            }
            
            // Apply late night fee if applicable
            const isLateNight = isLateNightTime(pickupTime);
            const lateNightFee = isLateNight ? LATE_NIGHT_FEE : 0;
            subtotal += lateNightFee;
            
            const totalPrice = subtotal * GRATUITY_MULTIPLIER;

            console.log('LivePriceQuoteTool Calculation:', {
              distanceInMiles: distanceInMiles.toFixed(2),
              mileageCost: mileageCost.toFixed(2),
              subtotal: subtotal.toFixed(2),
              minimumApplied,
              lateNightFee,
              totalPrice: totalPrice.toFixed(2)
            });

            // Get duration
            const durationText = element.duration?.text || 'N/A';

            setQuoteResult({
              price: parseFloat(totalPrice.toFixed(2)),
              distance: parseFloat(distanceInMiles.toFixed(1)),
              duration: durationText,
              minimumApplied,
              lateNightFee,
            });

            toast({
              title: "Quote Calculated",
              description: "Your estimated price is ready!",
            });
          } else {
            toast({
              title: "Route Not Found",
              description: "Unable to calculate route. Please check your addresses.",
              variant: "destructive",
            });
          }
        } else {
          toast({
            title: "Calculation Failed",
            description: "Unable to calculate distance. Please try again.",
            variant: "destructive",
          });
        }
      });
    } catch (error) {
      setIsCalculating(false);
      toast({
        title: "Error",
        description: "An error occurred while calculating your quote.",
        variant: "destructive",
      });
    }
  };

  return (
    <section className="py-20 bg-gradient-to-b from-background to-card/30">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12 space-y-4">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-foreground">
              Live Price Quote Tool
            </h2>
            <div className="w-24 h-1 bg-primary mx-auto" />
            <p className="text-lg text-muted-foreground">
              Get an instant price estimate for your ride
            </p>
          </div>

          <Card className="card-luxury border-primary/20 bg-card/90 backdrop-blur-xl">
            <CardContent className="p-8 md:p-12">
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="pickup-quote" className="text-primary font-semibold uppercase text-xs tracking-wider flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Pickup Address
                  </Label>
                  <Input
                    id="pickup-quote"
                    type="text"
                    placeholder="e.g., 123 Main St, San Antonio, TX"
                    value={pickupAddress}
                    onChange={(e) => setPickupAddress(e.target.value)}
                    className="bg-background/50 border-border/50 focus:border-primary h-12"
                    maxLength={200}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="dropoff-quote" className="text-primary font-semibold uppercase text-xs tracking-wider flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Drop-off Address
                  </Label>
                  <Input
                    id="dropoff-quote"
                    type="text"
                    placeholder="e.g., SAT Airport, San Antonio, TX"
                    value={dropoffAddress}
                    onChange={(e) => setDropoffAddress(e.target.value)}
                    className="bg-background/50 border-border/50 focus:border-primary h-12"
                    maxLength={200}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pickup-time-quote" className="text-primary font-semibold uppercase text-xs tracking-wider flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Pickup Time (Optional)
                  </Label>
                  <Input
                    id="pickup-time-quote"
                    type="time"
                    value={pickupTime}
                    onChange={(e) => setPickupTime(e.target.value)}
                    className="bg-background/50 border-border/50 focus:border-primary h-12"
                  />
                  <p className="text-xs text-muted-foreground">
                    Late night pickups (11 PM - 5 AM) include a $25 fee
                  </p>
                </div>

                <Button
                  onClick={calculateQuote}
                  className="w-full btn-luxury text-lg py-6 font-bold uppercase tracking-wider"
                  disabled={isCalculating}
                >
                  {isCalculating ? (
                    <>
                      <div className="w-5 h-5 border-2 border-background/30 border-t-background rounded-full animate-spin mr-2" />
                      Calculating...
                    </>
                  ) : (
                    <>
                      <DollarSign className="w-5 h-5 mr-2" />
                      Get Quote
                    </>
                  )}
                </Button>

                {quoteResult && (
                  <div className="mt-8 space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <div className="bg-primary/10 border-2 border-primary/30 rounded-lg p-6 space-y-4">
                      <h3 className="text-2xl font-bold text-center text-primary">
                        Your Estimated Quote
                      </h3>
                      
                      <div className="text-center">
                        <div className="text-5xl font-bold text-foreground mb-2">
                          ${quoteResult.price.toFixed(2)}
                        </div>
                        <div className="space-y-1">
                          {quoteResult.minimumApplied && (
                            <p className="text-sm text-emerald-500 font-medium">
                              ✓ SAT Airport Minimum ($85) Applied
                            </p>
                          )}
                          {quoteResult.lateNightFee && quoteResult.lateNightFee > 0 && (
                            <p className="text-sm text-amber-500 font-medium">
                              + Late Night Fee ($25)
                            </p>
                          )}
                          <p className="text-sm text-muted-foreground">
                            {quoteResult.minimumApplied 
                              ? 'Includes 20% gratuity'
                              : `Calculation: (${quoteResult.distance.toFixed(1)} miles × $3.50 + $15.00) + 20% gratuity`
                            }
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 pt-4 border-t border-primary/20">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <MapPin className="w-4 h-4 text-primary" />
                          <div>
                            <div className="text-xs uppercase tracking-wider text-primary font-semibold">Distance</div>
                            <div className="text-lg font-bold text-foreground">{quoteResult.distance} miles</div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Clock className="w-4 h-4 text-primary" />
                          <div>
                            <div className="text-xs uppercase tracking-wider text-primary font-semibold">Travel Time</div>
                            <div className="text-lg font-bold text-foreground">{quoteResult.duration}</div>
                          </div>
                        </div>
                      </div>

                      <div className="text-center pt-4 border-t border-primary/20">
                        <p className="text-xs text-muted-foreground">
                          * Final price may vary based on service type, time of day, and additional requirements.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default LivePriceQuoteTool;
